/* eslint-disable react-hooks/refs -- Stable chat callbacks intentionally read the latest render values through refs. */
import { useChat, type UseChatHelpers } from "@ai-sdk/react";
import {
  isToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  lastAssistantMessageIsCompleteWithToolCalls,
} from "ai";
import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  type AskUserQuestionResponse,
  ChatSDKError,
  type ChatMessage,
  DEFAULT_CHAT_MODEL,
  type PostChatRequestContext,
  isSelectedChatModel,
  resolveChatThinkingEffort,
} from "@tracify/shared";
import { usePostHog } from "posthog-js/react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useAuth } from "@lib/api-hooks";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { generateId } from "@lib/utils";
import { fetchWithErrorHandlers } from "@lib/ai/utils";
import { buildChatServerUrl } from "@lib/ai/chat-server-url";
import { getChatAgentId, type ChatAgent } from "@lib/ai/chat-agent";
import { chatModalModelAtom, chatThinkingEffortAtom } from "@/atoms";
import {
  captureDashboardCreated,
  captureDashboardDeleted,
  captureDashboardUpdated,
  captureInsightCreated,
  captureInsightUpdated,
  getRecordString,
  inferDashboardUpdateTypeFromUpdates,
} from "@/lib/insights/custom-insights-analytics";
import { createTracifyChatTransport } from "./chat-transport";
import { type ToolCallApprovalActionInput } from "./tool-call-approval";

const CHAT_MUTATION_TOOL_NAMES = new Set([
  "createDashboard",
  "updateDashboard",
  "deleteDashboard",
  "createInsight",
  "updateInsight",
  "addInsightToDashboard",
  "removeInsightFromDashboard",
]);

type ChatToolPart = Extract<
  ChatMessage["parts"][number],
  { type: `tool-${string}` }
>;

type QueuedToolContinuation = {
  type: "tool-approval";
  approvalId: string;
  approved: boolean;
};

type TracifyChatContextValue = {
  chatId: string;
  messages: ChatMessage[];
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  sendMessage: UseChatHelpers<ChatMessage>["sendMessage"];
  status: UseChatHelpers<ChatMessage>["status"];
  stop: UseChatHelpers<ChatMessage>["stop"];
  regenerate: UseChatHelpers<ChatMessage>["regenerate"];
  pendingAskUserQuestionId: string | null;
  submittedAskUserQuestionIds: ReadonlySet<string>;
  pendingToolApprovalId: string | null;
  submitAskUserQuestion: (input: {
    toolCallId: string;
    output: AskUserQuestionResponse;
  }) => Promise<void>;
  handleApproveToolCall: (input: ToolCallApprovalActionInput) => void;
  handleDenyToolCall: (input: ToolCallApprovalActionInput) => void;
};

const TracifyChatContext = createContext<TracifyChatContextValue | null>(null);

function isFailedToolOutput(
  output: unknown
): output is { ok: false; error?: unknown } {
  if (!output || typeof output !== "object") {
    return false;
  }

  return "ok" in output && output.ok === false;
}

function isSuccessfulMutationToolPart(
  part: ChatMessage["parts"][number]
): part is ChatToolPart {
  if (!isToolUIPart(part)) {
    return false;
  }

  const toolName = part.type.replace(/^tool-/, "");
  if (!CHAT_MUTATION_TOOL_NAMES.has(toolName)) {
    return false;
  }

  if (part.state !== "output-available") {
    return false;
  }

  return !isFailedToolOutput(part.output);
}

function invalidateChatQueries({
  chatId,
  queryClient,
  userId,
}: {
  chatId: string;
  queryClient: QueryClient;
  userId?: string;
}) {
  if (!userId) {
    return;
  }

  void queryClient.invalidateQueries({
    queryKey: ["ai-chat-detail", userId, chatId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["ai-chat-messages", userId, chatId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["ai-chat-thread", userId, chatId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["ai-chats", userId],
  });
}

function invalidateDashboardInsightQueriesForMutation({
  message,
  queryClient,
  selectedOrganisation,
}: {
  message: ChatMessage;
  queryClient: QueryClient;
  selectedOrganisation?: string | null;
}) {
  if (!selectedOrganisation) {
    return;
  }

  const hasSuccessfulMutation = (message.parts ?? []).some(
    isSuccessfulMutationToolPart
  );

  if (!hasSuccessfulMutation) {
    return;
  }

  const refetchType = "active" as const;

  void queryClient.invalidateQueries({
    queryKey: ["insights-library", selectedOrganisation],
    refetchType,
  });
  void queryClient.invalidateQueries({
    queryKey: ["dashboards", selectedOrganisation],
    refetchType,
  });
  void queryClient.invalidateQueries({
    queryKey: ["dashboard", selectedOrganisation],
    refetchType,
  });
}

function captureChatMutationAnalytics({
  message,
  posthog,
}: {
  message: ChatMessage;
  posthog: ReturnType<typeof usePostHog>;
}) {
  for (const part of message.parts ?? []) {
    if (!isSuccessfulMutationToolPart(part)) {
      continue;
    }

    const toolName = part.type.replace(/^tool-/, "");
    switch (toolName) {
      case "createDashboard": {
        const dashboardId = getRecordString(part.output, "dashboardId");
        if (!dashboardId) continue;
        captureDashboardCreated(posthog, {
          dashboardId,
          source: "ai_chat",
        });
        break;
      }
      case "updateDashboard": {
        const dashboardId = getRecordString(part.output, "dashboardId");
        if (!dashboardId) continue;
        captureDashboardUpdated(posthog, {
          dashboardId,
          source: "ai_chat",
          updateType: inferDashboardUpdateTypeFromUpdates(
            typeof part.input === "object" && part.input
              ? (part.input as Record<string, unknown>).updates
              : null
          ),
        });
        break;
      }
      case "deleteDashboard": {
        const dashboardId = getRecordString(part.output, "dashboardId");
        if (!dashboardId) continue;
        captureDashboardDeleted(posthog, {
          dashboardId,
          source: "ai_chat",
        });
        break;
      }
      case "createInsight": {
        const insightId = getRecordString(part.output, "insightId");
        if (!insightId) continue;
        captureInsightCreated(posthog, {
          insightId,
          source: "ai_chat",
        });
        break;
      }
      case "updateInsight": {
        const insightId = getRecordString(part.output, "insightId");
        if (!insightId) continue;
        captureInsightUpdated(posthog, {
          insightId,
          source: "ai_chat",
        });
        break;
      }
      case "addInsightToDashboard": {
        const dashboardId = getRecordString(part.output, "dashboardId");
        if (!dashboardId) continue;
        captureDashboardUpdated(posthog, {
          dashboardId,
          source: "ai_chat",
          updateType: "add_insight",
        });
        break;
      }
      case "removeInsightFromDashboard": {
        const dashboardId = getRecordString(part.output, "dashboardId");
        if (!dashboardId) continue;
        captureDashboardUpdated(posthog, {
          dashboardId,
          source: "ai_chat",
          updateType: "remove_insight",
        });
        break;
      }
      default:
        break;
    }
  }
}

function isChatContinuationBlocked(status: string) {
  return status === "submitted" || status === "streaming";
}

function getLatestAssistantMessage(messages: ChatMessage[]) {
  return [...messages]
    .reverse()
    .find((message) => message.role === "assistant");
}

export function TracifyChatProvider({
  children,
  chatId,
  selectedAgent,
  initialMessages,
  initialActiveStreamId,
}: {
  children: ReactNode;
  chatId: string;
  selectedAgent: ChatAgent;
  initialMessages?: ChatMessage[];
  initialActiveStreamId?: string | null;
}) {
  const chatModel = useAtomValue(chatModalModelAtom);
  const chatThinkingEffort = useAtomValue(chatThinkingEffortAtom);
  const { selectedOrganisation, selectedStoreIds } = useSelectedStores();
  const { data: auth } = useAuth();
  const userId = auth?.payload?.tid;
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const [pendingAskUserQuestionId, setPendingAskUserQuestionId] = useState<
    string | null
  >(null);
  const [submittedAskUserQuestionIds, setSubmittedAskUserQuestionIds] =
    useState<Set<string>>(() => new Set());
  const [pendingToolApprovalId, setPendingToolApprovalId] = useState<
    string | null
  >(null);
  const [queuedContinuations, setQueuedContinuations] = useState<
    Map<string, QueuedToolContinuation>
  >(() => new Map());
  const [cancelledActiveStreamId, setCancelledActiveStreamId] = useState<
    string | null
  >(null);
  const [bootMessages] = useState<ChatMessage[] | undefined>(initialMessages);
  const shouldResume = Boolean(
    initialActiveStreamId && initialActiveStreamId !== cancelledActiveStreamId
  );

  const requestContext = useMemo(() => {
    const selectedChatModel = isSelectedChatModel(chatModel)
      ? chatModel
      : DEFAULT_CHAT_MODEL;

    return {
      selectedChatModel,
      selectedThinkingEffort: resolveChatThinkingEffort(
        selectedChatModel,
        chatThinkingEffort
      ),
      sessionId: posthog?.get_session_id?.() || undefined,
      organisationId: selectedOrganisation ?? undefined,
      selectedStoreIds:
        selectedStoreIds.length > 0 ? selectedStoreIds : undefined,
    } satisfies PostChatRequestContext;
  }, [
    chatModel,
    chatThinkingEffort,
    posthog,
    selectedOrganisation,
    selectedStoreIds,
  ]);

  const authTokenRef = useRef(auth?.token ?? "");
  const requestContextRef = useRef<PostChatRequestContext>(requestContext);
  const selectedAgentRef = useRef(selectedAgent);
  const messagesRef = useRef<ChatMessage[]>(bootMessages ?? []);
  const statusRef = useRef<UseChatHelpers<ChatMessage>["status"]>("ready");
  const localStopRef = useRef<UseChatHelpers<ChatMessage>["stop"]>(
    async () => {}
  );
  const initialActiveStreamIdRef = useRef<string | null>(
    initialActiveStreamId ?? null
  );
  const cancelledActiveStreamIdRef = useRef<string | null>(null);

  authTokenRef.current = auth?.token ?? "";
  requestContextRef.current = requestContext;
  initialActiveStreamIdRef.current = initialActiveStreamId ?? null;
  cancelledActiveStreamIdRef.current = cancelledActiveStreamId;

  useEffect(() => {
    selectedAgentRef.current = selectedAgent;
  }, [selectedAgent]);

  const transport = useMemo(
    () =>
      createTracifyChatTransport({
        api: buildChatServerUrl("/api/chat"),
        fetch: fetchWithErrorHandlers,
        getAuthToken: () => authTokenRef.current,
        getAgentId: () => getChatAgentId(selectedAgentRef.current),
        getRequestContext: () => requestContextRef.current,
      }),
    []
  );

  const {
    messages,
    setMessages,
    sendMessage,
    addToolApprovalResponse,
    resumeStream,
    status,
    stop: localStop,
    regenerate,
  } = useChat<ChatMessage>({
    id: chatId,
    messages: bootMessages,
    resume: shouldResume,
    experimental_throttle: 100,
    generateId,
    transport,
    sendAutomaticallyWhen: (payload) =>
      lastAssistantMessageIsCompleteWithToolCalls(payload) ||
      lastAssistantMessageIsCompleteWithApprovalResponses(payload),
    onFinish: ({ message, isAbort, isDisconnect, isError }) => {
      if (isAbort || isDisconnect || isError) {
        return;
      }

      captureChatMutationAnalytics({
        message,
        posthog,
      });
      invalidateChatQueries({
        chatId,
        queryClient,
        userId,
      });
      invalidateDashboardInsightQueriesForMutation({
        message,
        queryClient,
        selectedOrganisation,
      });
    },
    onError: (error) => {
      if (error instanceof ChatSDKError || error instanceof Error) {
        toast.error(error.message);
        return;
      }

      toast.error("Failed to continue the chat.");
    },
  });

  messagesRef.current = messages;
  statusRef.current = status;
  localStopRef.current = localStop;

  const stop = useCallback(async () => {
    const activeStreamId = initialActiveStreamIdRef.current;
    const alreadyCancelled =
      activeStreamId && cancelledActiveStreamIdRef.current === activeStreamId;
    void localStopRef.current();

    if (alreadyCancelled) {
      return;
    }

    if (activeStreamId) {
      cancelledActiveStreamIdRef.current = activeStreamId;
      setCancelledActiveStreamId(activeStreamId);
    }

    if (!activeStreamId && !isChatContinuationBlocked(statusRef.current)) {
      return;
    }

    const assistantMessage = getLatestAssistantMessage(messagesRef.current);
    const body = assistantMessage ? { assistantMessage } : {};

    void fetchWithErrorHandlers(
      buildChatServerUrl(`/api/chat/${encodeURIComponent(chatId)}/cancel`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "tracify-token": authTokenRef.current,
        },
        body: JSON.stringify(body),
      }
    ).catch(() => {
      // The local stream is already stopped; server cancellation is best-effort.
    });
  }, [chatId]);

  const flushQueuedContinuation = useCallback(
    async (continuation: QueuedToolContinuation) => {
      try {
        await addToolApprovalResponse({
          id: continuation.approvalId,
          approved: continuation.approved,
        });
      } finally {
        setPendingToolApprovalId((current) =>
          current === continuation.approvalId ? null : current
        );
      }
    },
    [addToolApprovalResponse]
  );

  const enqueueToolContinuation = useCallback(
    (continuation: QueuedToolContinuation) => {
      setQueuedContinuations((current) => {
        if (current.has(continuation.approvalId)) {
          return current;
        }

        const next = new Map(current);
        next.set(continuation.approvalId, continuation);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (queuedContinuations.size === 0 || isChatContinuationBlocked(status)) {
      return;
    }

    const continuation = queuedContinuations.values().next().value;
    if (!continuation) {
      return;
    }

    setQueuedContinuations((current) => {
      if (!current.has(continuation.approvalId)) {
        return current;
      }

      const next = new Map(current);
      next.delete(continuation.approvalId);
      return next;
    });
    void flushQueuedContinuation(continuation);
  }, [flushQueuedContinuation, queuedContinuations, status]);

  const submitAskUserQuestion = useCallback(
    async (input: { toolCallId: string; output: AskUserQuestionResponse }) => {
      setPendingAskUserQuestionId(input.toolCallId);
      try {
        await fetchWithErrorHandlers(
          buildChatServerUrl(
            `/api/hooks/askUserQuestion/${encodeURIComponent(input.toolCallId)}`
          ),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "tracify-token": authTokenRef.current,
            },
            body: JSON.stringify(input.output),
          }
        );
        setSubmittedAskUserQuestionIds((current) => {
          if (current.has(input.toolCallId)) {
            return current;
          }
          return new Set(current).add(input.toolCallId);
        });
        await resumeStream();
      } catch (error) {
        setSubmittedAskUserQuestionIds((current) => {
          if (!current.has(input.toolCallId)) {
            return current;
          }
          const next = new Set(current);
          next.delete(input.toolCallId);
          return next;
        });
        throw error;
      } finally {
        setPendingAskUserQuestionId((current) =>
          current === input.toolCallId ? null : current
        );
      }
    },
    [resumeStream]
  );

  const handleApproveToolCall = useCallback(
    (input: ToolCallApprovalActionInput) => {
      setPendingToolApprovalId(input.approvalId);

      if (isChatContinuationBlocked(status)) {
        enqueueToolContinuation({
          type: "tool-approval",
          approvalId: input.approvalId,
          approved: true,
        });
        return;
      }

      void flushQueuedContinuation({
        type: "tool-approval",
        approvalId: input.approvalId,
        approved: true,
      });
    },
    [enqueueToolContinuation, flushQueuedContinuation, status]
  );

  const handleDenyToolCall = useCallback(
    (input: ToolCallApprovalActionInput) => {
      setPendingToolApprovalId(input.approvalId);

      if (isChatContinuationBlocked(status)) {
        enqueueToolContinuation({
          type: "tool-approval",
          approvalId: input.approvalId,
          approved: false,
        });
        return;
      }

      void flushQueuedContinuation({
        type: "tool-approval",
        approvalId: input.approvalId,
        approved: false,
      });
    },
    [enqueueToolContinuation, flushQueuedContinuation, status]
  );

  const value = useMemo<TracifyChatContextValue>(
    () => ({
      chatId,
      messages,
      setMessages,
      sendMessage,
      status,
      stop,
      regenerate,
      pendingAskUserQuestionId,
      submittedAskUserQuestionIds,
      pendingToolApprovalId,
      submitAskUserQuestion,
      handleApproveToolCall,
      handleDenyToolCall,
    }),
    [
      chatId,
      handleApproveToolCall,
      handleDenyToolCall,
      messages,
      pendingAskUserQuestionId,
      submittedAskUserQuestionIds,
      pendingToolApprovalId,
      regenerate,
      sendMessage,
      setMessages,
      status,
      stop,
      submitAskUserQuestion,
    ]
  );

  return createElement(TracifyChatContext.Provider, { value }, children);
}

export function useTracifyChat() {
  const context = useContext(TracifyChatContext);
  if (!context) {
    throw new Error("useTracifyChat must be used within TracifyChatProvider");
  }

  return context;
}
